import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ProgressLoader } from "@/components/molecules/ProgressLoader";
import { useAuth } from "@/features/auth/AuthProvider";
import type { Portal } from "@/features/auth/types";
import { errorMessage } from "@/lib/api/errors";
import { ROUTES, homeFor } from "./routes";

export function SessionLoader() {
  const { t } = useTranslation();
  return <ProgressLoader fullScreen messages={[t("loader.restoringSession"), t("loader.checkingAccess"), t("loader.almostDone")]} />;
}

/** Only signed-in users; others go to sign-in and come back afterwards. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <SessionLoader />;
  if (status === "anonymous") return <Navigate to={ROUTES.LOGIN} replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

/** Sign-in / registration pages: signed-in users are sent to their portal. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const { status, me } = useAuth();
  if (status === "loading") return <SessionLoader />;
  if (status === "authenticated") return <Navigate to={homeFor(me?.activePortal)} replace />;
  return <>{children}</>;
}

/**
 * Renders a portal only for users holding it. If the user holds the portal but
 * another one is active (e.g. an Admin Doctor opening /doctor), the session is
 * switched server-side first so permissions match the page.
 */
export function RequirePortal({ portal, children }: { portal: Portal; children: ReactNode }) {
  const { t } = useTranslation();
  const { me, switchPortal } = useAuth();
  const [failed, setFailed] = useState(false);
  const allowed = Boolean(me?.portals.includes(portal));
  const active = me?.activePortal === portal;

  useEffect(() => {
    if (allowed && !active && !failed) {
      switchPortal(portal).catch((error) => {
        toast.error(errorMessage(error, t("errors.generic")));
        setFailed(true);
      });
    }
  }, [allowed, active, failed, portal, switchPortal, t]);

  if (!allowed || failed) return <Navigate to={homeFor(me?.activePortal)} replace />;
  if (!active) return <ProgressLoader fullScreen messages={[t("loader.switchingPortal"), t("loader.almostDone")]} />;
  return <>{children}</>;
}

/** Hides a page from users lacking a database-driven permission (the API enforces it too). */
export function RequirePermission({ permission, children }: { permission: string; children: ReactNode }) {
  const { me, hasPermission } = useAuth();
  if (!hasPermission(permission)) return <Navigate to={homeFor(me?.activePortal)} replace />;
  return <>{children}</>;
}

/** "/" → the home page of the user's active portal. */
export function HomeRedirect() {
  const { status, me } = useAuth();
  if (status === "loading") return <SessionLoader />;
  return <Navigate to={status === "authenticated" ? homeFor(me?.activePortal) : ROUTES.LOGIN} replace />;
}
